import { useState } from 'react'
import {
  Building2,
  MapPin,
  Phone,
  Mail,
  Clock,
  Globe,
  Plus,
  Pencil,
  Trash2,
  X,
  Check,
  Star,
} from 'lucide-react'
import {
  useFactoryInfoList,
  useCreateFactoryInfo,
  useUpdateFactoryInfo,
  useDeleteFactoryInfo,
  type FactoryInfo,
  type FactoryInfoInput,
} from '@/hooks/useFactoryInfo'
import { cn } from '@/lib/utils'

const emptyForm: FactoryInfoInput = {
  name: '',
  address: '',
  city: '',
  country: '',
  phone: '',
  email: '',
  fax: '',
  working_hours: '',
  google_maps_url: '',
  is_primary: false,
}

export function FactoryInfoPage() {
  const { data: factories = [], isLoading } = useFactoryInfoList()
  const createFactory = useCreateFactoryInfo()
  const updateFactory = useUpdateFactoryInfo()
  const deleteFactory = useDeleteFactoryInfo()

  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<FactoryInfoInput>(emptyForm)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)

  const handleEdit = (factory: FactoryInfo) => {
    setForm({
      name: factory.name,
      address: factory.address,
      city: factory.city,
      country: factory.country,
      phone: factory.phone || '',
      email: factory.email,
      fax: factory.fax || '',
      working_hours: factory.working_hours || '',
      google_maps_url: factory.google_maps_url || '',
      is_primary: factory.is_primary,
    })
    setEditingId(factory.id)
    setShowForm(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (editingId) {
      await updateFactory.mutateAsync({ id: editingId, ...form })
    } else {
      await createFactory.mutateAsync(form)
    }

    setShowForm(false)
    setEditingId(null)
    setForm(emptyForm)
  }

  const handleDelete = async (id: string) => {
    await deleteFactory.mutateAsync(id)
    setDeleteConfirm(null)
  }

  const handleCancel = () => {
    setShowForm(false)
    setEditingId(null)
    setForm(emptyForm)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-10 h-10 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Factory Information</h1>
          <p className="text-secondary-500 mt-1">
            Manage factory/office locations displayed on the contact page
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Location
          </button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-secondary-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-secondary-900 mb-4">
            {editingId ? 'Edit Location' : 'Add New Location'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Location Name *
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g. Main Factory, Head Office"
                  required
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="contact@factory.com"
                  required
                />
              </div>

              {/* Address */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Address *
                </label>
                <input
                  type="text"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Street address"
                  required
                />
              </div>

              {/* City */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  City *
                </label>
                <input
                  type="text"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="City"
                  required
                />
              </div>

              {/* Country */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Country *
                </label>
                <input
                  type="text"
                  value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })}
                  className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Country"
                  required
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="+90 xxx xxx xx xx"
                />
              </div>

              {/* Fax */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Fax
                </label>
                <input
                  type="tel"
                  value={form.fax}
                  onChange={(e) => setForm({ ...form, fax: e.target.value })}
                  className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="+90 xxx xxx xx xx"
                />
              </div>

              {/* Working Hours */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Working Hours
                </label>
                <input
                  type="text"
                  value={form.working_hours}
                  onChange={(e) => setForm({ ...form, working_hours: e.target.value })}
                  className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Mon-Fri: 09:00-18:00"
                />
              </div>

              {/* Google Maps URL */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Google Maps URL
                </label>
                <input
                  type="url"
                  value={form.google_maps_url}
                  onChange={(e) => setForm({ ...form, google_maps_url: e.target.value })}
                  className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="https://maps.google.com/..."
                />
              </div>

              {/* Primary */}
              <div className="md:col-span-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_primary}
                    onChange={(e) => setForm({ ...form, is_primary: e.target.checked })}
                    className="w-4 h-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm text-secondary-700">
                    Set as primary location (shown prominently on contact page)
                  </span>
                </label>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 pt-4 border-t border-secondary-200">
              <button
                type="submit"
                disabled={createFactory.isPending || updateFactory.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                <Check className="w-4 h-4" />
                {editingId ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="flex items-center gap-2 px-4 py-2 border border-secondary-300 text-secondary-700 rounded-lg hover:bg-secondary-50 transition-colors"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Factory List */}
      {factories.length === 0 ? (
        <div className="bg-white rounded-xl border border-secondary-200 p-12 text-center">
          <Building2 className="w-12 h-12 text-secondary-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-secondary-900 mb-2">No locations added</h3>
          <p className="text-secondary-500 mb-4">
            Add your factory or office locations to display on the contact page.
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add First Location
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {factories.map((factory) => (
            <div
              key={factory.id}
              className={cn(
                'bg-white rounded-xl border p-6 transition-all',
                factory.is_primary
                  ? 'border-primary-300 ring-1 ring-primary-100'
                  : 'border-secondary-200'
              )}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className="text-lg font-semibold text-secondary-900">
                      {factory.name}
                    </h3>
                    {factory.is_primary && (
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full text-xs font-medium">
                        <Star className="w-3 h-3 fill-current" />
                        Primary
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div className="flex items-start gap-2 text-secondary-600">
                      <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>
                        {factory.address}
                        <br />
                        {factory.city}, {factory.country}
                      </span>
                    </div>

                    {factory.phone && (
                      <div className="flex items-center gap-2 text-secondary-600">
                        <Phone className="w-4 h-4 flex-shrink-0" />
                        <span>{factory.phone}</span>
                      </div>
                    )}

                    <div className="flex items-center gap-2 text-secondary-600">
                      <Mail className="w-4 h-4 flex-shrink-0" />
                      <span>{factory.email}</span>
                    </div>

                    {factory.fax && (
                      <div className="flex items-center gap-2 text-secondary-600">
                        <Phone className="w-4 h-4 flex-shrink-0" />
                        <span>Fax: {factory.fax}</span>
                      </div>
                    )}

                    {factory.working_hours && (
                      <div className="flex items-center gap-2 text-secondary-600">
                        <Clock className="w-4 h-4 flex-shrink-0" />
                        <span>{factory.working_hours}</span>
                      </div>
                    )}

                    {factory.google_maps_url && (
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 flex-shrink-0 text-secondary-600" />
                        <a
                          href={factory.google_maps_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:underline"
                        >
                          View on Google Maps
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEdit(factory)}
                    className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Pencil className="w-4 h-4 text-secondary-500" />
                  </button>

                  {deleteConfirm === factory.id ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleDelete(factory.id)}
                        disabled={deleteFactory.isPending}
                        className="p-2 bg-red-100 hover:bg-red-200 rounded-lg transition-colors"
                        title="Confirm delete"
                      >
                        <Check className="w-4 h-4 text-red-600" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(null)}
                        className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
                        title="Cancel"
                      >
                        <X className="w-4 h-4 text-secondary-500" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeleteConfirm(factory.id)}
                      className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4 text-secondary-500 hover:text-red-500" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
